import {
  addDoc,
  arrayUnion,
  collection,
  doc,
  getDocs,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../firebase";
import { dcGetBlob } from "../../services/districtCourtApi";

// Verify a downloaded blob is actually a PDF (not the eCourts
// "Orders is not uploaded" HTML/JSON error) before we persist it.
const isPdfBlob = async (blob) => {
  if (!blob || blob.size === 0) return false;
  try {
    const header = await blob.slice(0, 5).text();
    return header.startsWith("%PDF");
  } catch {
    return false;
  }
};

// Deterministic file name for a single court-order record, used both to store
// the file and to detect "already exported" (dedupe by name). District Court
// order records carry `srNo` + `orderDate` rather than the HC `order_no`.
export const districtCourtOrderFileName = (order) => {
  const dateSlug = String(order?.orderDate || "")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return `${dateSlug || "order"}_order_${order?.srNo ?? "x"}.pdf`;
};

// Get-or-create the per-case "Orders" folder (keyed on caseId + name + owner)
// so re-exports don't pile up duplicate folders. Mirrors highCourtOrderFiles.js.
const ensureOrdersFolder = async (caseId, caseNo, ownerId) => {
  const snap = await getDocs(
    query(
      collection(db, "folders"),
      where("caseId", "==", caseId),
      where("name", "==", "Orders"),
      where("lawyer_uid", "==", ownerId)
    )
  );
  if (!snap.empty) return snap.docs[0].id;
  const created = await addDoc(collection(db, "folders"), {
    name: "Orders",
    caseId,
    caseNo: caseNo || "",
    lawyer_uid: ownerId,
    createdAt: new Date().toISOString(),
  });
  return created.id;
};

// Export ONE District Court order record into an already-exported case:
//   1. fetch the order PDF (session-gated) and verify it's a real PDF,
//   2. upload it to Firebase Storage,
//   3. register it under the case's "Orders" folder (Files tab),
//   4. append it to the case doc's `orders` array (Orders tab) + files[].
// Deduped by file name — re-exporting the same order is a no-op.
// Returns { status: 'exported' | 'skipped' | 'no-pdf', fileName }.
export const exportSingleDistrictCourtOrder = async ({
  order,
  caseDoc,
  caseCollection,
  ownerId,
  sessionId,
}) => {
  const caseId = caseDoc.id;
  const caseNo = caseDoc.caseNo || "";
  const fileName = districtCourtOrderFileName(order);

  // Already exported? (same case + same file name). Query by caseId only (an
  // existing single-field index) and match the name client-side, so no new
  // Firestore composite index is needed.
  const dupSnap = await getDocs(
    query(collection(db, "files"), where("caseId", "==", caseId))
  );
  if (dupSnap.docs.some((d) => d.data().fileName === fileName)) {
    return { status: "skipped", fileName };
  }

  // pdf_params come from the parsed order row: copyOrders/displayPdf(normal_v,
  // case_val, court_code, filename, appFlag). The backend streams the PDF binary.
  const pdf = order.pdf_params || {};
  if (!pdf.normal_v || !pdf.case_val || !pdf.court_code || !pdf.filename) {
    return { status: "no-pdf", fileName };
  }
  const res = await dcGetBlob("/api/courtorders/order-pdf", {
    sessionId,
    normal_v: pdf.normal_v,
    case_val: pdf.case_val,
    court_code: pdf.court_code,
    filename: pdf.filename,
    appFlag: pdf.appFlag || "",
  });
  if (!res.ok || !res.blob || !(await isPdfBlob(res.blob))) {
    return { status: "no-pdf", fileName };
  }

  const safeCaseNo = String(caseNo || "case").replace(/[^a-zA-Z0-9._-]/g, "_");
  const path = `districtcourt-orders/${ownerId}/${safeCaseNo}/co_${order.srNo ?? 0}_${Date.now()}.pdf`;
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, res.blob, { contentType: "application/pdf" });
  const pdfStorageUrl = await getDownloadURL(storageRef);

  const folderId = await ensureOrdersFolder(caseId, caseNo, ownerId);
  await addDoc(collection(db, "files"), {
    caseNo,
    caseId,
    fileName,
    file_url: pdfStorageUrl,
    lawyer_uid: ownerId,
    uploadedAt: new Date().toISOString(),
    folderId,
    source: "districtcourt-court-order",
  });

  // Mirror into the case's Orders tab (case doc orders[]) using the same shape
  // the details screen renders, plus a permanent pdfStorageUrl for viewing.
  const orderEntry = {
    orderNumber: String(order.srNo ?? ""),
    orderOn: (order.ordersText || "Order").trim() || "Order",
    orderDate: order.orderDate || "",
    judge: "",
    orderLink: "",
    pdfProxyUrl: "",
    pdfStorageUrl,
    cino: "",
    source: "court-order",
  };
  await updateDoc(doc(db, caseCollection, caseId), {
    orders: arrayUnion(orderEntry),
    files: arrayUnion(pdfStorageUrl),
  });

  return { status: "exported", fileName };
};
